<template>
  <div>
    <div class="col-4" style="max-width: 300px;">
      <label for="select-indicator" style="font-size: 12px;">
        {{ this.$t('reports.indicators.indicatorLabel') }}:
      </label>
      <b-form-select
        v-model="indicatorSelected"
        id="select-indicator"
        :options="indicatorLabels"
        @change="selectIndicator"
        style="font-size: 12px;"
      ></b-form-select>
    </div>
    <div v-if="firstDisplayed" class="row mt-4">
      <div class="col-12">
        <div class="text-center">
          <p>
            <font-awesome-icon
              :icon="['fas', 'project-diagram']"
              style="margin-right: 20px; font-size: 35px;"
            />
          </p>
          <h5>
            {{ this.$t('reports.indicators.reportTitle') }}
          </h5>
          <p>{{ this.$t('reports.indicators.selIndicatorLabel') }}</p>
        </div>
      </div>
    </div>
    <div v-else-if="loader" class="row mb-4">
      <div class="col-12 text-center">
        <!-- <b-spinner
          variant="primary"
          type="grow"
          :label="`${this.$t('reports.loadingLabel')}...`"
        ></b-spinner> -->
        <b-table style="font-size: 12px;" striped :fields="fieldsLikeDislike" :items="[0, 1, 2, 3, 4]">
          <template v-slot:cell(indicator)>
            <idx-skeleton height="15" radius="10"></idx-skeleton>
          </template>
          <template v-slot:cell(description)>
            <idx-skeleton height="15" radius="10"></idx-skeleton>
          </template>
          <template v-slot:cell(answers)>
            <idx-skeleton height="15" radius="10"></idx-skeleton>
          </template>
        </b-table>
      </div>
    </div>
    <div v-else-if="this.items.length > 0">
      <div class="row mt-4">
        <div class="col-12">
          <b-table
            id="table-indicators"
            striped
            :fields="myMetric === 'like-dislike' ? fieldsLikeDislike : fields"
            :items="items"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :per-page="perPage"
            :current-page="currentPage"
            :busy="loading"
            responsive
            style="font-size: 12px;"
          >
            <template v-slot:cell(promotersPercent)="row">
              {{ row.item.promotersPercent }}%
            </template>
            <template v-slot:cell(neutralPercent)="row">
              {{ row.item.neutralPercent }}%
            </template>
            <template v-slot:cell(detractorsPercent)="row">
              {{ row.item.detractorsPercent }}%
            </template>
            <template v-slot:cell(nps)="row" style="font-size: 12px;">
              <radial-progress-bar
                :diameter="50"
                :stroke-width="5"
                innerStrokeColor="#f1f1f1"
                :startColor="npsColor(row.item.nps)"
                :stopColor="npsColor(row.item.nps)"
                :completed-steps="Number(row.item.nps)"
                :total-steps="totalSteps"
                :textSize="12"
              >
                <animated-number
                  :value="Number(row.item.nps)"
                  :formatValue="formatToPrice"
                  :duration="1000"
                />
                <b-tooltip target="number-nps" placement="bottom"> </b-tooltip>
              </radial-progress-bar>
            </template>
            <template v-slot:cell(download)="row">
              <button
                class="download-answers-icon"
                :title="`${$t('reports.indicators.answerDownloadlabel')}`"
                @click="downloadAnswers(row.item)"
                :disabled="loadingDownload"
              >
                <i class="fa fa-download" aria-hidden="true"></i>
              </button>
            </template>
          </b-table>
        </div>
      </div>
      <div class="row">
        <div class="col-12" style="font-size: 12px;">
          <b-pagination
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            aria-controls="table-indicators"
          ></b-pagination>
        </div>
        <!-- <div class="col-6 mt-2 text-right">
          <a
            @click="exportToXLS"
            :title="`${this.$t('reports.exportXlsLabel')}`"
          >
            <span class="xls">
              <i class="fas fa-file-download"></i>
            </span>
          </a>
        </div> -->
      </div>
    </div>
    <div v-else-if="!firstDisplayed" class="row mt-4">
      <div class="col-12 text-center">
        <p>
          <font-awesome-icon
            :icon="['fas', 'folder-open']"
            style="margin-right: 20px; font-size: 35px;"
          />
        </p>
        <h5>
          {{ this.$t('reports.indicators.noDatalabel') }}
        </h5>
        <p>
          {{ this.$t('reports.indicators.noAnswerLabel') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import XLSX from 'xlsx';
import swal from 'sweetalert';
import idxSkeleton from '@/components/molecules/idx-skeleton';
import { mapActions, mapState } from 'vuex';
import RadialProgressBar from 'vue-radial-progress';
import animatedNumber from 'animated-number-vue';
import api from '../../../../../../services/api';

const toLower = (text) => text.toString().toLowerCase();

const searchByName = (items, term) => {
  if (term) {
    return items.filter((item) =>
      toLower(`${item.description}`).includes(toLower(term)),
    );
  }

  return items;
};

export default {
  name: 'NPSByIndicatorsPanel',

  components: {
    RadialProgressBar,
    animatedNumber,
    idxSkeleton,
  },

  props: ['dataGraph'],

  data: () => ({
    loading: false,
    loadingDownload: false,
    sortBy: null,
    sortDesc: null,
    userAuth: null,
    search: null,
    loader: false,
    fields: [],
    items: [],
    keepItems: [],
    total: 0,
    currentPage: 1,
    perPage: 20,
    indicatorSelected: null,
    indicatorLabels: [],
    firstDisplayed: true,
    chartPromoters: '',
    chartPassives: '',
    chartDetrators: '',
    metricLabel: '',
    totalSteps: 100,
  }),

  created() {
    this.userAuth = JSON.parse(localStorage.getItem('_indecx'));
    this.changeLabel();
    this.fields = [
      {
        key: 'indicator',
        label: this.$t('reports.indicators.indicatorLabel'),
        sortable: true,
        class: 'text-center',
      },
      {
        key: 'description',
        label: this.$t('reports.indicators.descriptionLabel'),
        sortable: true,
        class: 'text-center',
      },
      {
        key: 'answers',
        label: this.$t('reports.answersLabel'),
        sortable: true,
        class: 'text-center',
      },
      {
        key: 'promotersPercent',
        label: `% ${this.chartPromoters}`,
        sortable: true,
        class: 'text-center',
      },
      {
        key: 'neutralPercent',
        label: `% ${this.chartPassives}`,
        sortable: true,
        class: 'text-center',
      },
      {
        key: 'detractorsPercent',
        label: `% ${this.chartDetrators}`,
        sortable: true,
        class: 'text-center',
      },
      {
        key: 'nps',
        label: this.metricLabel,
        sortable: true,
      },
      {
        key: 'download',
        label: this.$t('reports.downloadLabel'),
        class: 'text-center',
      },
    ];

    this.fieldsLikeDislike = [
      {
        key: 'indicator',
        label: this.$t('reports.indicators.indicatorLabel'),
        sortable: true,
        class: 'text-center',
      },
      {
        key: 'description',
        label: this.$t('reports.indicators.descriptionLabel'),
        sortable: true,
        class: 'text-center',
      },
      {
        key: 'answers',
        label: this.$t('reports.answersLabel'),
        sortable: true,
        class: 'text-center',
      },
      {
        key: 'promotersPercent',
        label: `% ${this.chartPromoters}`,
        sortable: true,
        class: 'text-center',
      },
      {
        key: 'detractorsPercent',
        label: `% ${this.chartDetrators}`,
        sortable: true,
        class: 'text-center',
      },
      {
        key: 'nps',
        label: this.metricLabel,
        sortable: true,
      },
      {
        key: 'download',
        label: this.$t('reports.downloadLabel'),
        class: 'text-center',
      },
    ];

    this.indicatorLabels = [
      {
        value: null,
        text: this.$t('reports.indicators.selIndicatorSelectLabel'),
      },
    ];
  },

  async mounted() {
    await this.getIndicators();
  },

  watch: {},

  computed: {
    ...mapState('calendar', ['calendarDates']),
  },

  methods: {
    ...mapActions('calendar', ['setDefaultDates']),

    returnMetric() {
      if (this.dataGraph.metric.indexOf('nps') !== -1) {
        return 'NPS';
      } else if (this.dataGraph.metric.indexOf('ces') !== -1) {
        return 'CES';
      } else if (this.dataGraph.metric.indexOf('csat') !== -1) {
        return 'CSAT';
      } else if (this.dataGraph.metric.indexOf('5-stars') !== -1) {
        return '5-Stars';
      } else if (this.dataGraph.metric.indexOf('like-dislike') !== -1) {
        return 'Like/Dislike';
      }
      return '';
    },

    async getIndicators() {
      this.indicatorSelected = null;
      this.indicatorLabels = [
        {
          value: null,
          text: this.$t('reports.indicators.selIndicatorSelectLabel'),
        },
      ];

      const response = await api.get(
        `indicators/distinct/${this.userAuth.companyId}`,
        {
          headers: {
            'x-access-token': this.userAuth.token,
          },
          params: {
            actionId: this.dataGraph.actionId
              ? this.dataGraph.actionId._id
              : null,
            groupId: this.dataGraph.actionGroupId
              ? this.dataGraph.actionGroupId._id
              : null,
            metric: this.dataGraph.metric,
            ih1: this.dataGraph.ih1 && this.dataGraph.ih1.length > 0 ? this.dataGraph.ih1.map((item) => item._id): null,
          },
        },
      );

      response.data.indicators.forEach((indicator) => {
        this.indicatorLabels.push({
          value: indicator.key,
          text: indicator.text,
        });
      });
    },

    async selectIndicator() {
      if (this.indicatorSelected) {
        this.loader = true;
        this.firstDisplayed = false;

        const response = await api.get(
          `${process.env.API_ROOT}/reports/metrics-by-indicators-panel/${this.userAuth.companyId}`,
          {
            headers: { 'x-access-token': this.userAuth.token },
            params: {
              actionId: this.dataGraph.actionId
                ? this.dataGraph.actionId._id
                : null,
              startDate: this.calendarDates.start
                ? this.calendarDates.start
                : null,
              endDate: this.calendarDates.end ? this.calendarDates.end : null,
              ih1: this.dataGraph.ih1 && this.dataGraph.ih1.length > 0 ? this.dataGraph.ih1.map((item) => item._id): null,
              metric: this.dataGraph.metric,
              groupId: this.dataGraph.actionGroupId
                ? this.dataGraph.actionGroupId._id
                : null,
              indicator: this.indicatorSelected,
              indicatorFilter: this.dataGraph.indicator
                ? this.dataGraph.indicator
                : null,
              indicatorValue: this.dataGraph.valueIndicator,
              period: this.dataGraph.period,
              _id: this.dataGraph._id,
            },
          },
        );

        this.items = response.data;
        this.loader = false;
      }
    },

    async downloadAnswers(item) {
      this.loadingDownload = true;

      this.apiServerlessDownloads = process.env.API_SERVERLESS_DOWNLOADS;

      try {
        await api.get(
          `${this.apiServerlessDownloads}/answers/download/${this.userAuth.companyId}/xlsx`,
          {
            headers: {
              'x-access-token': this.userAuth.token,
            },
            params: {
              indicator: item.indicator,
              indicatorValue: item.description,
              // indicatorRange: this.indicatorData.range,
              actionId: this.dataGraph.actionId
                ? this.dataGraph.actionId._id
                : null,
              startDate: this.calendarDates.start
                ? this.calendarDates.start
                : null,
              endDate: this.calendarDates.end ? this.calendarDates.end : null,
              ih1: this.dataGraph.ih1 && this.dataGraph.ih1.length > 0 ? this.dataGraph.ih1.map((item) => item._id): null,
              metric: this.dataGraph.metric,
              groupId: this.dataGraph.actionGroupId
                ? this.dataGraph.actionGroupId._id
                : null,
              npsByIndicatorReportFilter: this.dataGraph.indicator
                ? this.dataGraph.indicator
                : null,
              npsByIndicatorReportFilterValue: this.dataGraph.valueIndicator,
              period: this.dataGraph.period,
              _id: this.dataGraph._id,
            },
          },
        );

        swal({
          title: this.$t('reports.sucessLabel'),
          text: this.$t('reports.downloadMsg'),
          icon: 'success',
        });
      } catch (error) {
        swal({
          title: `${this.$t('reports.errorLabel')}...`,
          text: error.response.data.message,
          icon: 'error',
        });
      }

      this.loadingDownload = false;
    },

    npsColor(number) {
      let color;

      if (this.dataGraph.metric.indexOf('nps') !== -1) {
        if (number === 0) color = '#d62027';
        if (number < 10) color = '#ff0000';
        if (number >= 10 && number < 20) color = '#f05223';
        if (number >= 20 && number < 30) color = '#f36f21';
        if (number >= 30 && number < 40) color = '#faa823';
        if (number >= 40 && number < 50) color = '#ffca27';
        if (number >= 50 && number < 60) color = '#ecdb12';
        if (number >= 60 && number < 70) color = '#e8e73d';
        if (number >= 70 && number < 80) color = '#c5d92d';
        if (number >= 80 && number < 90) color = '#afd136';
        if (number >= 90 && number <= 100) color = '#4ce595';
      } else if (
        this.dataGraph.metric.indexOf('ces') !== -1 ||
        this.dataGraph.metric.indexOf('stars') !== -1 ||
        this.dataGraph.metric.indexOf('csat') !== -1
      ) {
        if (number <= 1) color = '#ff0000';
        if (number >= 1 && number < 2) color = '#f36f21';
        if (number >= 2 && number < 3) color = '#ffca27';
        if (number >= 3 && number < 4) color = '#e8e73d';
        if (number >= 4 && number < 5) color = '#afd136';
        if (number >= 5) color = '#4ce595';
      } else if (this.dataGraph.metric.indexOf('like-dislike') !== -1) {
        if (number <= 50) color = '#ff0000';
        if (number > 51) color = '#4ce595';
      }

      return color;
    },

    formatToPrice(value) {
      if (this.dataGraph.metric === 'csat-1-5(media)') {
        return value.toFixed(1);
      } else if (
        this.dataGraph.metric.indexOf('ces') !== -1 ||
        this.dataGraph.metric.indexOf('stars') !== -1
      ) {
        return value.toFixed(2);
      }
      return value.toFixed(0);
    },

    searchIndicator() {
      this.loading = true;

      this.items = searchByName(this.keepItems, this.search);

      if (this.search === '' || this.search === null) {
        this.items = this.keepItems;
      }

      this.loading = false;
    },

    resetSearch() {
      this.items = this.reportNpsIndicators;
      this.search = null;
    },

    s2ab(s) {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      // eslint-disable-next-line
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
      return buf;
    },

    exportToXLS() {
      const header = [
        this.$t('reports.indicators.indicatorLabel'),
        this.$t('reports.indicators.descriptionLabel'),
        this.$t('reports.answersLabel'),
        this.chartPromoters,
        this.chartPassives,
        this.chartDetrators,
        this.metricLabel,
      ];

      const arr = [];

      this.items.forEach((el) => {
        arr.push({
          [this.$t('reports.indicators.indicatorLabel')]: el.indicator,
          [this.$t('reports.indicators.descriptionLabel')]: el.description,
          [this.$t('reports.answersLabel')]: el.answers,
          [this.chartPromoters]: `${el.promotersPercent}%`,
          [this.chartPassives]: `${el.neutralPercent}%`,
          [this.chartDetrators]: `${el.detractorsPercent}%`,
          [this.metricLabel]: el.nps,
        });
      });

      const wb = XLSX.utils.book_new();

      wb.SheetNames.push('indicadores');

      const ws = XLSX.utils.json_to_sheet(arr, { header });

      wb.Sheets.indicadores = ws;

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });

      const uri = window.URL.createObjectURL(
        new Blob([this.s2ab(wbout)], { type: 'application/octet-stream' }),
      );

      const link = document.createElement('a');
      link.href = uri;
      link.setAttribute('download', 'indicadores.xlsx');
      document.body.appendChild(link);
      link.click();
    },

    changeLabel() {
      if (this.dataGraph.metric.indexOf('nps') !== -1) {
        this.chartPromoters = this.$t('reports.promoterLabel');
        this.chartPassives = this.$t('reports.neutralLabel');
        this.chartDetrators = this.$t('reports.detractorLabel');
        this.metricLabel = this.$t('reports.npsLabel');
        this.totalSteps = 100;
      } else if (this.dataGraph.metric.indexOf('ces') !== -1) {
        this.chartPromoters = '4-5';
        this.chartPassives = '3';
        this.chartDetrators = '1-2';
        this.metricLabel = this.$t('reports.cesLabel');
        this.totalSteps = 5;
      } else if (this.dataGraph.metric.indexOf('csat') !== -1) {
        this.chartPromoters = '4-5';
        this.chartPassives = '3';
        this.chartDetrators = '1-2';
        this.metricLabel = 'CSAT';
        this.totalSteps = 5;
      } else if (this.dataGraph.metric.indexOf('stars') !== -1) {
        this.chartPromoters = '4-5';
        this.chartPassives = '3';
        this.chartDetrators = '1-2';
        this.metricLabel = '5Stars';
        this.totalSteps = 5;
      } else if (this.dataGraph.metric.indexOf('like-dislike') !== -1) {
        this.chartPromoters = 'Like';
        this.chartDetrators = 'Dislike';
        this.metricLabel = 'Like/Dislike';
        this.totalSteps = 100;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/themes/default/variables.scss';

.download-answers-icon {
  padding: 5px 15px;
  background: $primary;
  color: $white;
  border-radius: 10px;
  font-size: 15px;
  border: none;
  transition: background-color 0.2s;

  &:hover {
    cursor: pointer;
    background: #1dc86e;
  }
}

.radial-progress-inner {
  span {
    font-size: 12px !important;
  }
}

.xls {
  font-size: 35px;
  color: $primary;
  cursor: pointer;

  transition: color 0.2s;

  &:hover {
    color: #1dc86e;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/molecules/highcharts/reports/indicators/panels/indicators.vue